// src/components/Contact.tsx
'use client';
import { useState, useEffect } from 'react';

export default function Contact() {
  const [messageStatus, setMessageStatus] = useState<'success' | 'error' | null>(null);
  const [isValidEmail, setIsValidEmail] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'email') {
      setIsValidEmail(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const email = formData.get('email') as string;
    const firstName = formData.get('first-name') as string;
    const message = formData.get('message') as string;

    if (!email || !firstName || !message) {
      alert('Please fill all required fields.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      alert('Please provide a valid email.');
      return;
    }

    try {
      await fetch('/api/mailer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          firstName,
          lastName: formData.get('last-name'),
          company: formData.get('company'),
          phoneNumber: formData.get('phone-number'),
          message,
        }),
      });
      setMessageStatus('success');
    } catch {
      setMessageStatus('error');
    }
    form.reset();
  };

  useEffect(() => {
    if (messageStatus) {
      const timer = setTimeout(() => setMessageStatus(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [messageStatus]);

  return (
      <div className="isolate bg-zinc-950 px-6 py-24 sm:py-32 lg:px-8">
        <div className="mx-auto max-w-3xl bg-white rounded-xl shadow-2xl overflow-hidden">
          <div className="mx-auto text-center mb-10 pt-8 max-w-3xl">
            <h2 className="text-3xl font-bold tracking-tight text-black sm:text-4xl">Contact</h2>
          </div>
          <form onSubmit={handleFormSubmit} className="p-8 sm:p-10">
            <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
              {/* First & Last Name */}
              <div>
                <label htmlFor="first-name" className="block text-sm font-semibold leading-6 text-gray-900">
                  First name
                </label>
                <input
                    type="text"
                    name="first-name"
                    id="first-name"
                    required
                    placeholder="First name"
                    className="mt-2.5 block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600"
                />
              </div>
              <div>
                <label htmlFor="last-name" className="block text-sm font-semibold leading-6 text-gray-900">
                  Last name
                </label>
                <input
                    type="text"
                    name="last-name"
                    id="last-name"
                    className="mt-2.5 block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
                />
              </div>

              {/* Company */}
              <div className="sm:col-span-2">
                <label htmlFor="company" className="block text-sm font-semibold leading-6 text-gray-900">
                  Company
                </label>
                <input
                    type="text"
                    name="company"
                    id="company"
                    className="mt-2.5 block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
                />
              </div>

              {/* Email */}
              <div className="sm:col-span-2">
                <label htmlFor="email" className="block text-sm font-semibold leading-6 text-gray-900">
                  Email
                </label>
                <input
                    type="email"
                    name="email"
                    id="email"
                    required
                    onChange={handleInputChange}
                    className={`mt-2.5 block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 ${
                        isValidEmail ? 'bg-green-100' : 'bg-red-100'
                    }`}
                />
              </div>

              {/* Message */}
              <div className="sm:col-span-2">
                <label htmlFor="message" className="block text-sm font-semibold leading-6 text-gray-900">
                  Message
                </label>
                <textarea
                    name="message"
                    id="message"
                    rows={4}
                    required
                    className="mt-2.5 block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
                />
              </div>
            </div>

            {/* Status Alerts */}
            <div className="mt-10">
              {messageStatus === 'success' && (
                  <div role="alert" className="alert alert-success">
                    Your message has been sent!
                  </div>
              )}
              {messageStatus === 'error' && (
                  <div role="alert" className="alert alert-error">
                    Error sending message!
                  </div>
              )}
            </div>

            {/* Submit Button */}
            <button
                type="submit"
                className="mt-10 block w-full rounded-md bg-gray-950 px-3.5 py-2.5 text-center text-sm font-semibold text-white hover:bg-gray-800 focus:outline-none"
            >
              Let’s talk
            </button>
          </form>
        </div>
      </div>
  );
}